import { Link } from '@inertiajs/react'
import { useEffect, useMemo, useState } from 'react'
import GoogleMap from '../../components/GoogleMap'

export default function Show({ queue }) {
  const [showDirections, setShowDirections] = useState(false)
  const toNumber = (value) => {
    const parsed = typeof value === 'number' ? value : Number.parseFloat(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  const initialEta = useMemo(() => {
    const position = queue?.position || 1
    return Math.max(position * 5, 1)
  }, [queue])

  const [etaMinutes, setEtaMinutes] = useState(initialEta)

  useEffect(() => {
    setEtaMinutes(initialEta)
  }, [initialEta])

  useEffect(() => {
    const interval = setInterval(() => {
      setEtaMinutes((prev) => Math.max(prev - 1, 0))
    }, 60000)

    return () => clearInterval(interval)
  }, [])

  const queuePoints = (() => {
    const lat = toNumber(queue?.venue?.latitude)
    const lng = toNumber(queue?.venue?.longitude)
    if (!queue || lat === null || lng === null) return []
    return [{
      id: queue.id,
      title: queue.offer?.offer_name || 'Queue',
      lat,
      lng,
      infoHtml: `<div><strong>${queue.offer?.offer_name || 'Queue'}</strong><br/>${queue.venue.name}</div>`,
    }]
  })()

  const destination = queuePoints.length > 0
    ? { lat: queuePoints[0].lat, lng: queuePoints[0].lng }
    : null

  if (!queue) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-3xl mx-auto bg-white rounded-xl shadow p-8 text-center">
          <p className="text-xl text-gray-600">Queue not found.</p>
          <Link href="/queues" className="inline-block mt-4 text-blue-600 hover:text-blue-800">
            ← Back to Active Queues
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Active Queue</h1>
            <p className="text-gray-600 mt-2">Queue ID #{queue.id}</p>
          </div>
          <div className="flex gap-3">
            <Link
              href="/"
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded-lg"
            >
              Offers
            </Link>
            <Link
              href="/queues"
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg"
            >
              Active Queues
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-8">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {queue.offer?.offer_name || 'Offer'}
              </h2>
              <p className="text-gray-600 mt-1">
                Status: <span className="font-semibold capitalize">{queue.status || 'active'}</span>
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Your Position</p>
              <p className="text-4xl font-bold text-blue-600">{queue.position}</p>
              <p className="text-sm text-gray-500 mt-2">ETA ~ {etaMinutes} min</p>
            </div>
          </div>

          {queue.venue && (
            <div className="mt-6 bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-semibold text-gray-500 uppercase">Venue</p>
              <p className="text-lg text-gray-900">{queue.venue.name}</p>
            </div>
          )}

          <div className="mt-6 flex gap-3">
            {queue.offer?.id && (
              <Link
                href={`/offers/${queue.offer.id}`}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded-lg"
              >
                View Offer
              </Link>
            )}
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg"
              disabled
            >
              Check In (Coming Soon)
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-4 mt-6">
          {destination && !showDirections && (
            <button
              onClick={() => setShowDirections(true)}
              className="mb-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg w-full"
            >
              📍 Get Directions to Venue
            </button>
          )}
          <GoogleMap
            points={queuePoints}
            enableDirections={showDirections && Boolean(destination)}
            destination={destination}
            useCurrentLocation={showDirections && Boolean(destination)}
            className="h-80 w-full rounded-lg"
          />
        </div>
      </div>
    </div>
  )
}
